import {
  AlertTriangle,
  Calendar,
  Clock,
  Edit3,
  Plus,
  Save,
  Stethoscope,
  Trash2,
} from "lucide-react";
import React, { useState } from "react";
import { useLocaleFormat } from "../../hooks/useLocaleFormat";
import {
  healthConditionsStrings,
  type HealthConditionsStrings,
  isBuiltInLabel,
} from "../../locales/modules/healthConditions";
import { useI18nStore } from "../../stores/i18nStore";
import { MedicalProcedure } from "../../types/fhir-health";

interface MedicalProceduresProps {
  procedures: MedicalProcedure[];
  onSave: (procedures: MedicalProcedure[]) => void;
  isEditing: boolean;
  onToggleEdit: () => void;
  isSaving: boolean;
}

// Common procedures with CPT codes. The code identifies the entry; the name
// is a translation key, and the English label is what gets stored as the
// record's display text. `legacy` lists German names older versions stored
// that differ from today's German label.
type ProcedureKey = keyof HealthConditionsStrings;

const COMMON_PROCEDURES: {
  code: string;
  system: string;
  nameKey: ProcedureKey;
  category: MedicalProcedure["category"];
  legacy?: string[];
}[] = [
  // Outpatient procedures
  {
    code: "71020",
    system: "CPT",
    nameKey: "procNameChestXray",
    category: "outpatient",
  },
  {
    code: "80053",
    system: "CPT",
    nameKey: "procNameBloodPanel",
    category: "outpatient",
  },
  {
    code: "93000",
    system: "CPT",
    nameKey: "procNameEcg",
    category: "outpatient",
  },
  {
    code: "76700",
    system: "CPT",
    nameKey: "procNameAbdominalUltrasound",
    category: "outpatient",
  },
  {
    code: "99213",
    system: "CPT",
    nameKey: "procNamePreventiveCheckup",
    category: "outpatient",
  },
  {
    code: "77057",
    system: "CPT",
    nameKey: "procNameMammography",
    category: "outpatient",
  },
  {
    code: "88150",
    system: "CPT",
    nameKey: "procNamePapSmear",
    category: "outpatient",
  },
  {
    code: "82270",
    system: "CPT",
    nameKey: "procNameColorectalScreening",
    category: "outpatient",
  },

  // Ambulatory procedures
  {
    code: "97110",
    system: "CPT",
    nameKey: "procNamePhysicalTherapy",
    category: "ambulatory",
  },
  {
    code: "90471",
    system: "CPT",
    nameKey: "procNameVaccination",
    category: "ambulatory",
  },
  {
    code: "12001",
    system: "CPT",
    nameKey: "procNameWoundCare",
    category: "ambulatory",
  },
  {
    code: "20610",
    system: "CPT",
    nameKey: "procNameJointAspiration",
    category: "ambulatory",
  },
  {
    code: "64483",
    system: "CPT",
    nameKey: "procNamePainInjection",
    category: "ambulatory",
  },
  {
    code: "45378",
    system: "CPT",
    nameKey: "procNameColonoscopy",
    category: "ambulatory",
  },

  // Inpatient procedures
  {
    code: "44970",
    system: "CPT",
    nameKey: "procNameAppendectomy",
    category: "inpatient",
  },
  {
    code: "47562",
    system: "CPT",
    nameKey: "procNameGallbladderSurgery",
    category: "inpatient",
    legacy: ["Gallenblasen-OP"],
  },
  {
    code: "29881",
    system: "CPT",
    nameKey: "procNameKneeArthroscopy",
    category: "inpatient",
  },
  {
    code: "66984",
    system: "CPT",
    nameKey: "procNameCataractSurgery",
    category: "inpatient",
    legacy: ["Grauer-Star-OP"],
  },
  {
    code: "19301",
    system: "CPT",
    nameKey: "procNameMastectomy",
    category: "inpatient",
  },
  {
    code: "70450",
    system: "CPT",
    nameKey: "procNameHeadCt",
    category: "inpatient",
    legacy: ["CT-Kopf"],
  },
  {
    code: "72148",
    system: "CPT",
    nameKey: "procNameSpineMri",
    category: "inpatient",
    legacy: ["MRT-Wirbelsäule"],
  },

  // Emergency procedures
  {
    code: "80061",
    system: "CPT",
    nameKey: "procNameEmergencyBloodTest",
    category: "emergency",
  },
];

const CATEGORY_KEYS: Record<string, ProcedureKey> = {
  outpatient: "procCategoryOutpatient",
  inpatient: "procCategoryInpatient",
  ambulatory: "procCategoryAmbulatory",
  emergency: "procCategoryEmergency",
};

const STATUS_KEYS: Record<string, ProcedureKey> = {
  completed: "procStatusCompleted",
  "in-progress": "procStatusInProgress",
  preparation: "procStatusPreparation",
  "not-done": "procStatusNotDone",
  "on-hold": "procStatusOnHold",
  stopped: "procStatusStopped",
  "entered-in-error": "procStatusEnteredInError",
  unknown: "procStatusUnknown",
};

export const MedicalProceduresComponent: React.FC<MedicalProceduresProps> = ({
  procedures,
  onSave,
  isEditing,
  onToggleEdit,
  isSaving,
}) => {
  const { t } = useI18nStore();
  const fmt = useLocaleFormat();
  const label = (map: Record<string, ProcedureKey>, code?: string) => {
    const key = code ? map[code] : undefined;
    return key ? t[key] : (code ?? "");
  };

  // Built-in procedures are shown in the active language, also for records
  // that older versions stored with a German name; free text stays as typed.
  const procedureName = (procedure: MedicalProcedure) => {
    const entry = COMMON_PROCEDURES.find((c) => c.code === procedure.code);
    if (
      entry &&
      isBuiltInLabel(entry.nameKey, procedure.display, entry.legacy)
    ) {
      return t[entry.nameKey];
    }
    return procedure.display;
  };
  const [localProcedures, setLocalProcedures] =
    useState<MedicalProcedure[]>(procedures);

  const addNewProcedure = () => {
    const dateValue = new Date().toISOString().split("T")[0];
    const newProcedure: MedicalProcedure = {
      id: crypto.randomUUID(),
      code: "",
      display: "",
      category: "outpatient",
      status: "completed",
      ...(dateValue && { performedDateTime: dateValue }),
      performer: "",
    };
    setLocalProcedures([...localProcedures, newProcedure]);
  };

  const updateProcedure = (
    index: number,
    field: keyof MedicalProcedure,
    value: string | boolean | Date,
  ) => {
    const updated = [...localProcedures];
    const current = updated[index];

    if (field === "code") {
      // Auto-fill procedure name, system and category
      const procedure = COMMON_PROCEDURES.find((p) => p.code === value);
      if (procedure && current) {
        updated[index] = {
          ...current,
          code: value,
          display: healthConditionsStrings.en[procedure.nameKey],
          system: procedure.system,
          category: procedure.category,
        } as MedicalProcedure;
      } else if (current) {
        updated[index] = { ...current, [field]: value } as MedicalProcedure;
      }
    } else if (current) {
      updated[index] = { ...current, [field]: value } as MedicalProcedure;
    }
    setLocalProcedures(updated);
  };

  const removeProcedure = (index: number) => {
    const updated = localProcedures.filter((_, i) => i !== index);
    setLocalProcedures(updated);
  };

  const handleSave = () => {
    onSave(localProcedures);
  };

  const getCategoryColor = (category?: string) => {
    switch (category) {
      case "outpatient":
        return "text-primary-600 bg-primary-50 border-primary-200";
      case "inpatient":
        return "text-red-600 bg-red-50 border-red-200";
      case "ambulatory":
        return "text-green-600 bg-green-50 border-green-200";
      case "emergency":
        return "text-yellow-600 bg-yellow-50 border-yellow-200";
      default:
        return "text-gray-600 bg-gray-50 border-gray-200";
    }
  };

  const getStatusColor = (status?: string) => {
    switch (status) {
      case "completed":
        return "text-green-700";
      case "in-progress":
        return "text-primary-700";
      case "preparation":
        return "text-yellow-700";
      case "cancelled":
        return "text-red-700";
      case "on-hold":
        return "text-gray-700";
      default:
        return "text-gray-700";
    }
  };

  const getCategoryIcon = (category?: string) => {
    switch (category) {
      case "outpatient":
        return "🔍";
      case "inpatient":
        return "🏥";
      case "ambulatory":
        return "⚕️";
      case "emergency":
        return "🚨";
      default:
        return "📋";
    }
  };

  const getStatusIcon = (status?: string) => {
    switch (status) {
      case "completed":
        return "✅";
      case "in-progress":
        return "⏳";
      case "preparation":
        return "📋";
      case "not-done":
        return "❌";
      case "on-hold":
        return "⏸️";
      case "stopped":
        return "⏹️";
      case "entered-in-error":
        return "📝";
      case "unknown":
        return "❓";
      default:
        return "❓";
    }
  };

  const parseProcedureDate = (date?: string) => {
    if (!date) return null;
    if (/^\d{4}-\d{2}-\d{2}$/.test(date)) {
      const [year = 1970, month = 1, day = 1] = date.split("-").map(Number);
      return new Date(year, month - 1, day);
    }
    const parsed = new Date(date);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  };

  const formatProcedureDate = (date?: string) => {
    const parsed = parseProcedureDate(date);
    return parsed ? fmt.date(parsed) : t.procNoDate;
  };

  const isUpcoming = (date?: string) => {
    const parsed = parseProcedureDate(date);
    return !!parsed && parsed > new Date();
  };

  const isRecent = (date?: string) => {
    const procedureDate = parseProcedureDate(date);
    if (!procedureDate) return false;
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
    return procedureDate >= thirtyDaysAgo && procedureDate <= new Date();
  };

  // Sort procedures by date (newest first)
  const sortedProcedures = [...procedures].sort(
    (a, b) =>
      (parseProcedureDate(b.performedDateTime)?.getTime() ?? 0) -
      (parseProcedureDate(a.performedDateTime)?.getTime() ?? 0),
  );

  return (
    <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-bold text-gray-900 flex items-center space-x-2">
          <Stethoscope className="w-5 h-5 text-primary-600" />
          <span>{t.medicalProcedures || "Medical Procedures"}</span>
        </h2>
        <div className="flex space-x-2">
          <button
            onClick={onToggleEdit}
            className="p-2 text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
            title={isEditing ? t.cancel : t.edit}
            aria-label={isEditing ? t.cancel : t.edit}
          >
            <Edit3 className="w-4 h-4" />
          </button>
          {isEditing && (
            <button
              onClick={handleSave}
              disabled={isSaving}
              className="px-3 py-1 text-sm bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:bg-gray-400 transition-colors flex items-center space-x-1"
            >
              {isSaving ? (
                <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white"></div>
              ) : (
                <Save className="w-3 h-3" />
              )}
              <span>{isSaving ? t.saving : t.save}</span>
            </button>
          )}
        </div>
      </div>

      {isEditing ? (
        <div className="space-y-6">
          {localProcedures.map((procedure, index) => (
            <div
              key={procedure.id || index}
              className="border border-gray-200 rounded-lg p-4"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label
                    htmlFor={`procedure-${index}-category`}
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    {t.procFieldCategory}
                  </label>
                  <select
                    id={`procedure-${index}-category`}
                    value={procedure.category}
                    onChange={(e) =>
                      updateProcedure(index, "category", e.target.value)
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  >
                    {Object.entries(CATEGORY_KEYS).map(([value, key]) => (
                      <option key={value} value={value}>
                        {t[key]}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label
                    htmlFor={`procedure-${index}-status`}
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    {t.status}
                  </label>
                  <select
                    id={`procedure-${index}-status`}
                    value={procedure.status}
                    onChange={(e) =>
                      updateProcedure(index, "status", e.target.value)
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  >
                    {Object.entries(STATUS_KEYS).map(([value, key]) => (
                      <option key={value} value={value}>
                        {t[key]}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="md:col-span-2">
                  <label
                    htmlFor={`procedure-${index}-code`}
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    {t.procFieldProcedure}
                  </label>
                  <select
                    id={`procedure-${index}-code`}
                    value={procedure.code}
                    onChange={(e) =>
                      updateProcedure(index, "code", e.target.value)
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  >
                    <option value="">{t.pleaseSelect}</option>
                    {COMMON_PROCEDURES.filter(
                      (p) => p.category === procedure.category,
                    ).map((proc) => (
                      <option key={proc.code} value={proc.code}>
                        {t[proc.nameKey]} ({proc.code})
                      </option>
                    ))}
                  </select>
                  {procedure.code &&
                    !COMMON_PROCEDURES.find(
                      (p) => p.code === procedure.code,
                    ) && (
                      <input
                        type="text"
                        value={procedure.display}
                        onChange={(e) =>
                          updateProcedure(index, "display", e.target.value)
                        }
                        className="w-full px-3 py-2 mt-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        placeholder={t.procCustomPlaceholder}
                      />
                    )}
                </div>

                <div>
                  <label
                    htmlFor={`procedure-${index}-performed-date`}
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    {t.procFieldDate}
                  </label>
                  <input
                    id={`procedure-${index}-performed-date`}
                    type="date"
                    value={
                      procedure.performedDateTime
                        ? procedure.performedDateTime.split("T")[0]
                        : ""
                    }
                    onChange={(e) =>
                      updateProcedure(
                        index,
                        "performedDateTime",
                        e.target.value,
                      )
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label
                    htmlFor={`procedure-${index}-performer`}
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    {t.procFieldPerformer}
                  </label>
                  <input
                    id={`procedure-${index}-performer`}
                    type="text"
                    value={procedure.performer}
                    onChange={(e) =>
                      updateProcedure(index, "performer", e.target.value)
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder={t.procPerformerPlaceholder}
                  />
                </div>

                <div className="md:col-span-2">
                  <label
                    htmlFor={`procedure-${index}-location`}
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    {t.procFieldLocation}
                  </label>
                  <input
                    id={`procedure-${index}-location`}
                    type="text"
                    value={procedure.location || ""}
                    onChange={(e) =>
                      updateProcedure(index, "location", e.target.value)
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder={t.procLocationPlaceholder}
                  />
                </div>

                <div className="md:col-span-2">
                  <label
                    htmlFor={`procedure-${index}-reason`}
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    {t.procFieldReason}
                  </label>
                  <input
                    id={`procedure-${index}-reason`}
                    type="text"
                    value={procedure.reasonCode || ""}
                    onChange={(e) =>
                      updateProcedure(index, "reasonCode", e.target.value)
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder={t.procReasonPlaceholder}
                  />
                </div>

                <div className="md:col-span-2">
                  <label
                    htmlFor={`procedure-${index}-outcome`}
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    {t.procFieldOutcome}
                  </label>
                  <textarea
                    id={`procedure-${index}-outcome`}
                    value={procedure.outcome || ""}
                    onChange={(e) =>
                      updateProcedure(index, "outcome", e.target.value)
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    rows={2}
                    placeholder={t.procOutcomePlaceholder}
                  />
                </div>

                <div className="md:col-span-2">
                  <label
                    htmlFor={`procedure-${index}-complication`}
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    {t.procFieldComplications}
                  </label>
                  <textarea
                    id={`procedure-${index}-complication`}
                    value={procedure.complication || ""}
                    onChange={(e) =>
                      updateProcedure(index, "complication", e.target.value)
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    rows={2}
                    placeholder={t.procComplicationsPlaceholder}
                  />
                </div>

                <div className="md:col-span-2">
                  <label
                    htmlFor={`procedure-${index}-note`}
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    {t.procFieldNotes}
                  </label>
                  <textarea
                    id={`procedure-${index}-note`}
                    value={procedure.note || ""}
                    onChange={(e) =>
                      updateProcedure(index, "note", e.target.value)
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    rows={2}
                    placeholder={t.procNotesPlaceholder}
                  />
                </div>
              </div>

              <div className="flex justify-end mt-4">
                <button
                  onClick={() => removeProcedure(index)}
                  className="text-red-600 hover:text-red-800 p-1"
                  title={t.procRemove}
                  aria-label={t.procRemove}
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}

          <button
            onClick={addNewProcedure}
            className="w-full px-4 py-2 border border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-primary-500 hover:text-primary-600 transition-colors flex items-center justify-center space-x-2"
          >
            <Plus className="w-4 h-4" />
            <span>{t.procAdd}</span>
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          {sortedProcedures.length === 0 ? (
            <p className="text-gray-500 italic">
              {t.noMedicalProceduresDocumented}
            </p>
          ) : (
            sortedProcedures.map((procedure, index) => {
              const categoryColor = getCategoryColor(procedure.category);
              const statusColor = getStatusColor(procedure.status);
              const categoryIcon = getCategoryIcon(procedure.category);
              const statusIcon = getStatusIcon(procedure.status);
              const upcoming = isUpcoming(procedure.performedDateTime);
              const recent = isRecent(procedure.performedDateTime);

              return (
                <div
                  key={procedure.id || index}
                  className={`border rounded-lg p-4 ${categoryColor} ${upcoming ? "ring-2 ring-yellow-400" : ""}`}
                >
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center justify-between">
                        <h4 className="font-medium text-gray-900 flex items-center space-x-2">
                          <span className="text-lg">{categoryIcon}</span>
                          <span>{procedureName(procedure)}</span>
                          {procedure.code && (
                            <span className="text-xs px-2 py-1 bg-white bg-opacity-50 rounded">
                              {procedure.code}
                            </span>
                          )}
                        </h4>
                        <div className="flex items-center space-x-2">
                          {upcoming && (
                            <div className="flex items-center space-x-1 text-yellow-700 bg-yellow-50 px-2 py-1 rounded text-xs">
                              <Clock className="w-3 h-3" />
                              <span>{t.procBadgeUpcoming}</span>
                            </div>
                          )}
                          {recent && (
                            <div className="flex items-center space-x-1 text-green-700 bg-green-50 px-2 py-1 rounded text-xs">
                              <span>✨ {t.procBadgeNew}</span>
                            </div>
                          )}
                          <span
                            className={`text-xs px-2 py-1 rounded bg-white bg-opacity-50 ${statusColor} flex items-center space-x-1`}
                          >
                            <span>{statusIcon}</span>
                            <span>
                              {procedure.status && STATUS_KEYS[procedure.status]
                                ? label(STATUS_KEYS, procedure.status)
                                : t.procStatusUnknown}
                            </span>
                          </span>
                          <div className="text-xs px-2 py-1 rounded bg-white bg-opacity-50">
                            {label(CATEGORY_KEYS, procedure.category)}
                          </div>
                        </div>
                      </div>

                      <div className="mt-2 text-sm text-gray-700">
                        <div className="flex items-center justify-between">
                          <span className="flex items-center space-x-1">
                            <Calendar className="w-3 h-3" />
                            <span>
                              {formatProcedureDate(procedure.performedDateTime)}
                            </span>
                          </span>
                          <span>{procedure.performer}</span>
                        </div>
                      </div>

                      {procedure.location && (
                        <div className="mt-1 text-sm text-gray-600">
                          <strong>{t.procLocationLabel}</strong>{" "}
                          {procedure.location}
                        </div>
                      )}

                      {procedure.reasonCode && (
                        <div className="mt-1 text-sm text-gray-600">
                          <strong>{t.procIndicationLabel}</strong>{" "}
                          {procedure.reasonCode}
                        </div>
                      )}

                      {procedure.outcome && (
                        <div className="mt-2 text-sm text-gray-700">
                          <strong>{t.procOutcomeLabel}</strong>{" "}
                          {procedure.outcome}
                        </div>
                      )}

                      {procedure.complication && (
                        <div className="mt-2 text-sm text-red-700 bg-red-50 p-2 rounded flex items-start space-x-2">
                          <AlertTriangle className="w-4 h-4 mt-0.5" />
                          <div>
                            <strong>{t.procComplicationsLabel}</strong>{" "}
                            {procedure.complication}
                          </div>
                        </div>
                      )}

                      {procedure.note && (
                        <div className="mt-2 text-xs text-gray-600 italic">
                          {procedure.note}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      )}
    </div>
  );
};
